import { ArrowRight, ListChecks } from "lucide-react";

import { EmptyState } from "@/components/layout/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/** Mirror of flowstack.flow.schema.FlowNodeSchema (subset).
 * Read defensively — flow templates can declare nodes with fanout
 * blocks the preview just labels.
 *
 * Per Rupert PR-261: dropped the speculative ``fanin`` field — it
 * isn't on FlowNodeSchema today, the chip never rendered, and
 * declaring it here misled anyone reading the component as the
 * source of truth on Flow topology. Add it back the day the
 * backend schema gains the field. */
interface FlowNode {
  name: string;
  tile: string;
  description?: string;
  fanout?: unknown;
}

/** Mirror of flowstack.flow.schema.FlowParameter. */
interface FlowParameter {
  type: string;
  source?: string;
  description?: string;
  default?: unknown;
  required?: boolean;
}

interface FlowInput {
  type: string;
  description?: string;
}

/**
 * Read-only preview of a FlowSchema. Surfaces:
 *   - Top section: flow inputs (typed-object names + descriptions)
 *   - Middle section: flow-level user-facing parameters
 *   - Bottom section: per-node table with tile-name chips +
 *     fanout / singleton topology badges
 *
 * The full Flow submission form (user supplies typed-object inputs
 * + parameter values + watches per-Tile cost breakdown) lands in
 * the follow-up sub-PR; this preview is the shipping piece that
 * unblocks the picker.
 */
export interface FlowManifestPreviewProps {
  manifest: Record<string, unknown> | undefined;
  isLoading?: boolean;
}

export function FlowManifestPreview({
  manifest,
  isLoading,
}: FlowManifestPreviewProps): JSX.Element {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-9 w-1/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!manifest) {
    return (
      <EmptyState
        title="Pick a Flow first."
        description="Once a Flow is selected, its declared inputs, parameters, and node graph appear here."
      />
    );
  }

  const inputs = (manifest.inputs ?? {}) as Record<string, FlowInput>;
  const parameters = (manifest.parameters ?? {}) as Record<string, FlowParameter>;
  const nodes = (manifest.nodes ?? []) as FlowNode[];

  const inputEntries = Object.entries(inputs);
  const userParams = Object.entries(parameters).filter(([, p]) => (p.source ?? "user") === "user");

  return (
    <div className="flex flex-col gap-6">
      <Section title="Inputs" count={inputEntries.length}>
        {inputEntries.length === 0 ? (
          <p className="text-xs text-muted-foreground">No top-level inputs declared.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {inputEntries.map(([name, input]) => (
              <li
                key={name}
                className="flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-surface-2/50 px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-medium text-foreground">{name}</span>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" aria-hidden />
                  <span className="font-mono text-[11px] text-muted-foreground">{input.type}</span>
                </div>
                {input.description && (
                  <span className="line-clamp-1 text-[11px] text-muted-foreground">
                    {input.description}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Parameters" count={userParams.length}>
        {userParams.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            This Flow takes no user-facing parameters.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {userParams.map(([name, p]) => (
              <li
                key={name}
                className="flex items-start justify-between gap-3 rounded-md border border-border-subtle bg-surface-2/50 px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-medium text-foreground">{name}</span>
                  <span className="rounded-sm bg-surface-3 px-1 font-mono text-[10px] text-muted-foreground">
                    {p.type}
                  </span>
                  {p.required && (
                    <span className="text-[10px] uppercase tracking-wider text-state-failed-validation-fg">
                      required
                    </span>
                  )}
                </div>
                {p.description && (
                  <span className="line-clamp-2 max-w-md text-[11px] text-muted-foreground">
                    {p.description}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Nodes"
        count={nodes.length}
        icon={<ListChecks className="h-3.5 w-3.5" aria-hidden />}
      >
        <div className="overflow-x-auto">
          <Table role="table">
            <TableHeader>
              <TableRow>
                <TableHead>Node</TableHead>
                <TableHead>Tile</TableHead>
                <TableHead>Topology</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {nodes.map((node) => (
                <TableRow key={node.name} interactive="hover">
                  <TableCell className="font-mono text-xs">{node.name}</TableCell>
                  <TableCell className="font-mono text-xs">{node.tile}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {node.fanout ? (
                        <span className="rounded-full border border-state-staging-border bg-state-staging-bg px-1.5 py-0.5 text-[10px] text-state-staging-fg">
                          fanout
                        </span>
                      ) : (
                        <span className="rounded-full border border-border-subtle bg-surface-2 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                          singleton
                        </span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Section>
    </div>
  );
}

interface SectionProps {
  title: string;
  count?: number;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

function Section({ title, count, icon, children }: SectionProps): JSX.Element {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        {icon}
        <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </h4>
        {count !== undefined && (
          <span className="rounded-sm bg-surface-2 px-1 font-mono text-[10px] text-muted-foreground">
            {count}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}
